import { User, RolePermission } from '../types';

/**
 * Extracts all permissions from a user's staff profile.
 */
export const getPermissions = (user: User | null): string[] => {
  if (!user || !user.staffProfile || !user.staffProfile.staffRoleAssignments) {
    return [];
  }

  const permissions = new Set<string>();

  user.staffProfile.staffRoleAssignments.forEach((assignment) => {
    if (assignment.isActive && assignment.staffRole && assignment.staffRole.rolePermissions) {
      assignment.staffRole.rolePermissions.forEach((permission: RolePermission) => {
        permissions.add(permission.key);
      });
    }
  });

  return Array.from(permissions);
};

/**
 * Checks if a user has a specific permission.
 */
export const hasPermission = (user: User | null, permissionKey: string): boolean => {
  if (!user) return false;
  
  // Admin bypass if needed, but let's stick to explicit permissions first
  // if (user.role === 'admin') return true;

  const permissions = getPermissions(user);
  return permissions.includes(permissionKey);
};

/**
 * Checks if a user has any of the specified permissions.
 */
export const hasAnyPermission = (user: User | null, permissionKeys: string[]): boolean => {
  if (!user) return false;
  
  const permissions = getPermissions(user);
  return permissionKeys.some((key) => permissions.includes(key));
};

/**
 * Checks if a user has all of the specified permissions.
 */
export const hasAllPermissions = (user: User | null, permissionKeys: string[]): boolean => {
  if (!user) return false;
  
  const permissions = getPermissions(user);
  return permissionKeys.every((key) => permissions.includes(key));
};
/**
 * Checks if a user has the 'admin' role key in their active assignments.
 */
export const checkIsAdmin = (user: User | null): boolean => {
  if (!user || !user.staffProfile || !user.staffProfile.staffRoleAssignments) {
    return false;
  }

  return user.staffProfile.staffRoleAssignments.some(
    (assignment) => assignment.isActive && assignment.staffRole?.key === 'admin'
  );
};
